import { computed, shallowRef, unref } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth, useEntityPermissions } from '@/composables/useAuth';
import { shellToolbarActions } from '../data/shellConfig';
import { shellExportMenuItems } from '../data/shellExportConfig';

/** Handlers registered by active shell tab views (e.g. TableView → add employee). */
const actionHandlers = shallowRef(new Map());
const exportHandlers = shallowRef(new Map());
const columnVisibilityMenuSource = shallowRef(null);

/**
 * Bridges ShellToolbar actions to the active tab view.
 * Views register handlers on activate and unregister on deactivate (keep-alive safe).
 */
export function useShellToolbar() {
  const route = useRoute();
  const employeesPermissions = useEntityPermissions('employees');
  const departmentsPermissions = useEntityPermissions('departments');
  const positionsPermissions = useEntityPermissions('positions');
  const { isSuperAdmin, hasPermission } = useAuth();

  const activeShellTab = computed(() => route.meta?.shellTab || null);

  const canUseToolbarAction = (action, tab) => {
    if (action.key === 'export') {
      if (isSuperAdmin.value) return true;
      if (tab === 'tree') return true;
      if (tab === 'departments') return departmentsPermissions.canRead.value;
      if (tab === 'positions') return positionsPermissions.canRead.value;
      return employeesPermissions.canRead.value;
    }
    if (action.key === 'importEmployees') {
      return isSuperAdmin.value || hasPermission('employees.import');
    }
    if (action.entity === 'employees') {
      if (action.entityAction === 'update') return employeesPermissions.canUpdate.value;
      return employeesPermissions.canCreate.value;
    }
    if (action.entity === 'departments') {
      if (action.entityAction === 'update') return departmentsPermissions.canUpdate.value;
      return departmentsPermissions.canCreate.value;
    }
    if (action.entity === 'positions') {
      if (action.entityAction === 'update') return positionsPermissions.canUpdate.value;
      return positionsPermissions.canCreate.value;
    }
    return true;
  };

  const registerActionHandler = (key, handler) => {
    const next = new Map(actionHandlers.value);
    next.set(key, handler);
    actionHandlers.value = next;
  };

  const unregisterActionHandler = (key) => {
    if (!actionHandlers.value.has(key)) return;
    const next = new Map(actionHandlers.value);
    next.delete(key);
    actionHandlers.value = next;
  };

  const registerExportHandler = (key, handler) => {
    const next = new Map(exportHandlers.value);
    next.set(key, handler);
    exportHandlers.value = next;
  };

  const unregisterExportHandler = (key) => {
    if (!exportHandlers.value.has(key)) return;
    const next = new Map(exportHandlers.value);
    next.delete(key);
    exportHandlers.value = next;
  };

  const registerColumnVisibilityMenu = (source) => {
    columnVisibilityMenuSource.value = source;
  };

  const unregisterColumnVisibilityMenu = () => {
    columnVisibilityMenuSource.value = null;
  };

  const clearActionHandlers = () => {
    actionHandlers.value = new Map();
    exportHandlers.value = new Map();
    columnVisibilityMenuSource.value = null;
  };

  const columnVisibilityMenuItems = computed(() => {
    const source = columnVisibilityMenuSource.value;
    if (!source) return [];
    return unref(source);
  });

  const columnVisibilityEnabled = computed(() => columnVisibilityMenuItems.value.length > 0);

  const invokeAction = (key) => {
    actionHandlers.value.get(key)?.();
  };

  const invokeExport = (key) => {
    exportHandlers.value.get(key)?.();
  };

  const visibleExportMenuItems = computed(() => {
    const tab = activeShellTab.value;
    return shellExportMenuItems
      .filter((item) => !item.tabs || (tab && item.tabs.includes(tab)))
      .map((item) => {
        const hasHandler = exportHandlers.value.has(item.key);
        return {
          ...item,
          disabled: !hasHandler,
          tooltip: hasHandler ? item.label : 'Not available on this tab',
        };
      });
  });

  const exportActionEnabled = computed(() =>
    visibleExportMenuItems.value.some((item) => !item.disabled)
  );

  const visibleToolbarActions = computed(() => {
    const tab = activeShellTab.value;
    return shellToolbarActions
      .filter((action) => !action.tabs || (tab && action.tabs.includes(tab)))
      .filter((action) => canUseToolbarAction(action, tab))
      .map((action) => {
        const hasHandler = actionHandlers.value.has(action.key);
        const comingSoon = action.comingSoon === true;
        const isExportAction = action.key === 'export';
        const isColumnVisibilityAction = action.key === 'columnVisibility';
        const isTabSupported = !action.tabs || (tab && action.tabs.includes(tab));
        const disabled =
          comingSoon ||
          !isTabSupported ||
          (isExportAction
            ? !exportActionEnabled.value
            : isColumnVisibilityAction
              ? !columnVisibilityEnabled.value
              : !hasHandler);

        let tooltip = action.label;
        if (comingSoon) tooltip = 'Coming soon';
        else if (!isTabSupported) tooltip = 'Not available on this tab';
        else if (isExportAction && !exportActionEnabled.value) {
          tooltip = 'Export not available on this tab';
        } else if (isColumnVisibilityAction && !columnVisibilityEnabled.value) {
          tooltip = 'Column visibility not available on this tab';
        } else if (!hasHandler && !isExportAction && !isColumnVisibilityAction) {
          tooltip = 'Not available on this tab';
        } else if (action.key === 'importEmployees') tooltip = 'Import employees from Excel';
        else if (action.key === 'addEmployee') tooltip = 'Add a new employee';
        else if (action.key === 'addDepartment') tooltip = 'Add a new department';
        else if (action.key === 'addPosition') tooltip = 'Add a new position';

        return {
          ...action,
          disabled,
          tooltip,
        };
      });
  });

  return {
    activeShellTab,
    visibleExportMenuItems,
    columnVisibilityMenuItems,
    registerActionHandler,
    unregisterActionHandler,
    registerExportHandler,
    unregisterExportHandler,
    registerColumnVisibilityMenu,
    unregisterColumnVisibilityMenu,
    clearActionHandlers,
    invokeAction,
    invokeExport,
    visibleToolbarActions,
  };
}
